import React, { useEffect, useState, useCallback } from 'react';
import MapCard from './cards/MapCard';
import OperationReportsCard, { Range } from './cards/OperationReportCard';
import PlateInventoryCard from './cards/PlateInventoryCard';
import ReportsAuditCard from './cards/ReportsAuditCard';
import SystemHealthCard from './cards/SystemHealthCard';
import RequestToExecutionCard from './cards/RequestToExecutionCard';
import ResponseTimeAveragesCard from './cards/ResponseTimeAveragesCard';
import TopIsolationPointsCard from './cards/TopIsolationPointsCard';
import SpadesMostUsedCard from './cards/SpadesMostUsedCard';

import SpadesOverviewCard, { SpadeOverview } from './cards/SpadesOverviewCard';
import GlobalFilters from './filters/GlobalFilters';
import { FilterState, defaultFilters } from './filters/types';
import mapImg from '../../assets/map.png';

/* ───────── Persian digits helpers (UI-only) ───────── */
const faNum = (v: number | string | null | undefined) => {
  if (v === null || v === undefined) return '';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^\d.-]/g, ''));
  if (!Number.isNaN(n)) return new Intl.NumberFormat('fa-IR').format(n);
  return String(v).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
};
const faDate = (d: Date | string | number) => new Date(d).toLocaleDateString('fa-IR');
(() => {
  const orig = Number.prototype.toLocaleString;
  Number.prototype.toLocaleString = function (locales?: any, options?: any) {
    try { return orig.call(this, 'fa-IR', options); } catch { return orig.call(this, locales, options); }
  };
})();
/* ─────────────────────────────────────────────────── */

type Props = {
  onRecentActivitiesClick: () => void;
  debug?: boolean;
  logoSrc?: string;
};

type UserInfo = {
  Id?: number;
  UnitArea?: string;       // نمایش UI (مثلاً "واحد PP")
  Username?: string;
  FullName?: string;
  Role?: number;           // 0=مدیر ، 1=بهره‌بردار
};

type RequestItem = {
  id?: number | string;
  unit?: string;
  title?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

const REQUESTS_ENDPOINT = "api/operations/recent";

const mapRole = (role?: number | string) =>
  String(role) === '0' ? 'مدیر' : 'بهره بردار';

const Dashboard: React.FC<Props> = ({
  onRecentActivitiesClick,
  debug = false,
  logoSrc,
}) => {
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [range, setRange] = useState<Range>('24h');

  type Stats = {
    total_spades_installed?: number;
    operations_last_24h?: number;
    operations_in_range?: number;
    recent_activities?: number;
    my_open_tasks?: number;
    recent_completed?: number;
    overall_progress_pct?: number;
    spades_information?: any;
  };
  const [stats, setStats] = useState<Stats>({});

  const [overview, setOverview] = useState<SpadeOverview>({
    total: 0,
    installed: 0,
    warehouse: 0,
    personnel: 0,
    need_repair: 0,
    dropped: 0,
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [mapFull, setMapFull] = useState(false);

  // ─────── نقش و واحد کاربر ───────
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const roleName = mapRole(userInfo.Role);
  const isManager = String(userInfo.Role) === '0';
  const userUnit = userInfo.UnitArea || undefined;

  // ─────── پنل نمایش درخواست‌ها (۴۸ ساعت اخیر) ───────
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsErr, setRequestsErr] = useState<string | null>(null);
  const [requests, setRequests] = useState<RequestItem[]>([]);

  useEffect(() => {
    if (!mapFull) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMapFull(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapFull]);

  const toRFC3339 = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

  /** Map UI unit label → backend value (drop "واحد ", force PP uppercase) */
  const mapUnit = (name: string) => {
    const cleaned = name.replace(/^واحد\s*/i, '').trim();
    return cleaned.toLowerCase() === 'pp' ? 'PP' : cleaned;
  };

  /** ---------- GET query builder (USES mapUnit) ---------- */
  const buildUrlWithFilters = useCallback((base: string) => {
    const now = new Date();
    const toISODate = (date: Date) => date.toISOString().slice(0, 10);
    const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

    let startISO: string | undefined;
    let endISO: string | undefined;

    switch (filters.preset) {
      case 'today': { const t = toISODate(now); startISO = t; endISO = t; break; }
      case 'yesterday': { const y = toISODate(addDays(now, -1)); startISO = y; endISO = y; break; }
      case 'last7': { startISO = toISODate(addDays(now, -6)); endISO = toISODate(now); break; }
      case 'last30': { startISO = toISODate(addDays(now, -29)); endISO = toISODate(now); break; }
      case 'last90': { startISO = toISODate(addDays(now, -89)); endISO = toISODate(now); break; }
      case 'last365': { startISO = toISODate(addDays(now, -364)); endISO = toISODate(now); break; }
      case 'custom': { startISO = filters.start!; endISO = filters.end!; break; }
      default: { startISO = toISODate(addDays(now, -6)); endISO = toISODate(now); }
    }

    const qs = new URLSearchParams();
    if (startISO) qs.set('start', toRFC3339(new Date(`${startISO}T00:00:00Z`)));
    if (endISO) qs.set('end', toRFC3339(new Date(`${endISO}T23:59:59Z`)));
    if (filters.units.length) qs.set('unit_area', filters.units.map(mapUnit).join(','));  // ← map here
    if (filters.operationTypes.length) qs.set('ops', filters.operationTypes.join(','));
    if (filters.user) qs.set('user', filters.user);

    return `${base}?${qs.toString()}`;
  }, [filters]);

  /** ---------- POST body builder for stats (already mapped) ---------- */
  const buildStatsPostBody = useCallback(() => {
    const now = new Date();
    const toISODate = (date: Date) => date.toISOString().slice(0, 10);
    const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

    let startISO: string;
    let endISO: string;

    switch (filters.preset) {
      case 'today': { const t = toISODate(now); startISO = t; endISO = t; break; }
      case 'yesterday': { const y = toISODate(addDays(now, -1)); startISO = y; endISO = y; break; }
      case 'last7': { startISO = toISODate(addDays(now, -6)); endISO = toISODate(now); break; }
      case 'last30': { startISO = toISODate(addDays(now, -29)); endISO = toISODate(now); break; }
      case 'last90': { startISO = toISODate(addDays(now, -89)); endISO = toISODate(now); break; }
      case 'last365': { startISO = toISODate(addDays(now, -364)); endISO = toISODate(now); break; }
      case 'custom':
      default: { startISO = filters.start || toISODate(addDays(now, -6)); endISO = filters.end || toISODate(now); }
    }

    const body: any = {
      start: toRFC3339(new Date(`${startISO}T00:00:00Z`)),
      end: toRFC3339(new Date(`${endISO}T23:59:59Z`)),
    };
    if (filters.units.length) body.unit_area = filters.units.map(mapUnit);
    if (filters.operationTypes.length) body.ops = filters.operationTypes;
    if (filters.user) body.user = filters.user;

    if (debug) {
      console.groupCollapsed('📡 POST /api/dashboard/stats');
      console.log('body:', body);
      console.groupEnd();
    }
    return body;
  }, [filters, debug]);

  // ─────── گرفتن اطلاعات کاربر ───────
  const loadUserInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const r = await fetch('/api/user/my-info', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      const t = await r.text();
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);
      const j = t ? JSON.parse(t) : {};
      setUserInfo({
        Id: j?.Id ?? j?.id,
        UnitArea: j?.UnitArea ?? j?.unit_area ?? j?.unit,
        Username: j?.Username ?? j?.username,
        FullName: j?.FullName ?? j?.full_name,
        Role: j?.Role ?? j?.role,
      });
    } catch {
      setUserInfo((u) => ({ ...u, Role: u.Role ?? 1 }));
    }
  }, []);

  // ─────── محدودیت نقش روی واحد ───────
  useEffect(() => {
    const unit = userInfo.UnitArea;
    if (isManager) setFilters((f) => ({ ...f, units: [] }));
    else if (unit) setFilters((f) => ({ ...f, units: [unit] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  /** ------------------- DATA LOAD (15s polling) ------------------- */
  const load = useCallback(async () => {
  setLoading(true); setErr(null);

  const token = localStorage.getItem('token') || '';
  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchCount = async (path: string) => {
    const url = buildUrlWithFilters(path);
    const res = await fetch(url, { headers, credentials: 'include' });
    const text = await res.text();
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
    const json = text ? JSON.parse(text) : [];
    const arr = Array.isArray(json)
      ? json
      : json?.items ?? json?.data?.items ?? json?.operations ?? json?.data?.operations ?? [];
    return Array.isArray(arr) ? arr.length : 0;
  };

  try {
    // NOTE: /api/dashboard has been REMOVED
    const [statsPart, recentCnt, openCnt, finishedCnt] = await Promise.all([
      (async () => {
        const r = await fetch('/api/dashboard/stats', {
          method: 'POST', headers, credentials: 'include',
          body: JSON.stringify(buildStatsPostBody()),
        });
        const t = await r.text();
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);
        const j = t ? JSON.parse(t) : {};
        return {
          total_spades_installed: j?.total_spades_installed ?? 0,
          operations_in_range: j?.operations_in_range ?? j?.operations_last_24h ?? 0,
          operations_last_24h: j?.operations_last_24h ?? 0,
          overall_progress_pct: j?.overall_progress_pct,
          spades_information: j?.spades_information ?? {},
          spades_size_counts: j?.spades_size_counts ?? []
        } as Stats & { spades_information?: any };
      })(),
      fetchCount('/api/operations/recent?limit=10'),
      fetchCount('/api/operation-log/worker'),
      fetchCount('/api/operation-log/finished'),
    ]);

    const sp = (statsPart as any)?.spades_information ?? {};

    setOverview({
      total: Number(sp?.total_spades ?? 0),
      installed: Number(sp?.total_spades_installed ?? 0),
      warehouse: Number(sp?.total_healthy_in_ware_house ?? sp?.total_spades_in_ware_house ?? 0),
      personnel: Number(sp?.total_spades_user_hold ?? 0),
      need_repair: Number(sp?.total_need_repair ?? 0),
      dropped: Number(sp?.total_wear_out ?? 0),
    });

    setStats({
      total_spades_installed: Number((statsPart as any)?.total_spades_installed ?? 0),
      operations_in_range: Number((statsPart as any)?.operations_in_range ?? 0),
      operations_last_24h: Number((statsPart as any)?.operations_last_24h ?? 0),
      overall_progress_pct: (statsPart as any)?.overall_progress_pct,
      recent_activities: recentCnt,
      my_open_tasks: openCnt,
      recent_completed: finishedCnt,
      spades_information: sp,
    });
  } catch (e: any) {
    setErr(e?.message || 'خطا در دریافت داده‌های داشبورد');
    setStats({});
  } finally {
    setLoading(false);
  }
}, [buildUrlWithFilters, buildStatsPostBody]);


  // بارگذاری نقش/واحد و سپس دیتا
  useEffect(() => { loadUserInfo(); }, [loadUserInfo]);
  useEffect(() => {
    let timer: number | undefined;
    const run = async () => { await load(); timer = window.setInterval(load, 36000000); };
    run();
    return () => { if (timer) window.clearInterval(timer); };
  }, [load, range]);

  // ─────── بارگذاری درخواست‌ها (۴۸ ساعت اخیر) ───────
const openRequests = useCallback(async () => {
  setRequestsOpen(true);
  setRequestsLoading(true);
  setRequestsErr(null);

  try {
    const token = localStorage.getItem('token') || '';
    const headers: HeadersInit = {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const now = new Date();
    const start = new Date(now.getTime() - 48 * 3600_000);

    const qs = new URLSearchParams();
    qs.set('start', toRFC3339(start));
    qs.set('end', toRFC3339(now));
    qs.set('limit', '50');

    // مدیر: همه ↔ بهره‌بردار: فقط واحد خودش
    if (!isManager && userUnit) {
      qs.set('unit_area', mapUnit(userUnit));
    }

    const url = `${REQUESTS_ENDPOINT}?${qs.toString()}`;

    const r = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    const t = await r.text();
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);
    const j = t ? JSON.parse(t) : {};

    const list: any[] =
      Array.isArray(j)
        ? j
        : j?.items ?? j?.data?.items ?? j?.operations ?? j?.data?.operations ?? [];




const mapStatusText = (s: number | string | null | undefined) => {
  if (s === null || s === undefined) return '—';
  const v = Number(s);

  switch (v) {
    case 0: return 'در حال انجام';
    case 1: return 'رد شده';
    case 2: return 'تایید شده';
    case 3: return 'تخصیص داده شده';
    case 4: return 'در انتظار اسپید';
    case 5: return 'دریافت اسپید';
    case 6: return 'پایان یافته';
    default: return `وضعیت ${v}`;
  }
};

const mapOperationType = (t: number | string | null | undefined) => {
  if (t === null || t === undefined) return '';
  const v = Number(t);

  switch (v) {
    case 0: return 'نصب';
    case 1: return 'نصب اجباری';
    case 2: return 'برداشت';
    default: return '';
  }
};


const mapRequestItem = (x: any): RequestItem => {
  const typeTxt = mapOperationType(x?.Type ?? x?.operation_type) || '—';

  return {
    id: x?.id ?? x?.Id ?? x?.operation_id ?? x?.request_id ?? x?.WorkOrderId,
    unit: x?.unit ?? x?.unit_area ?? x?.UnitArea ?? x?.unitName,
    title: typeTxt, // ❗ فقط نوع عملیات
    status: mapStatusText(x?.status ?? x?.state ?? x?.current_status ?? x?.Status),
    created_at: x?.created_at ?? x?.CreatedAt ?? x?.createdAt,
    updated_at: x?.updated_at ?? x?.UpdatedAt ?? x?.updatedAt,
  };
};


    setRequests(list.map(mapRequestItem));
  } catch (e: any) {
    setRequestsErr(e?.message || 'خطا در دریافت عملیات‌های اخیر');
    setRequests([]);
  } finally {
    setRequestsLoading(false);
  }
}, [isManager, userUnit]);

  /** ------------------- STYLES ------------------- */
  const SIDEBAR_W = 220;
  const COLORS = {
    bg: '#0f172a',
    panel: '#111827',
    panel2: '#0b1220',
    text: '#e5e7eb',
    sub: '#94a3b8',
    accent: '#60a5fa',
    accent2: '#22d3ee',
    danger: '#f87171',
    ok: '#34d399',
    border: 'rgba(255,255,255,0.08)',
  };

  const pageStyle: React.CSSProperties = {
    background: COLORS.bg,
    color: COLORS.text,
    minHeight: '100vh',
    fontFamily:
      '"B Titr", "BTitrBold", "IRANSans", "Inter", system-ui, "Segoe UI", Roboto, sans-serif',
    fontWeight: 900,
    position: 'relative',
    paddingRight: SIDEBAR_W + 16,
  };

  const header: React.CSSProperties = {
    padding: '18px 24px',
    borderBottom: `1px solid ${COLORS.border}`,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto 1fr',
    gap: 12,
  };

  const titleBox: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 16px',
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    background: 'rgba(96,165,250,0.15)',
    color: COLORS.text,
    boxShadow: '0 6px 18px rgba(0,0,0,.20)',
    minWidth: 360,
  };

  const gridWrap: React.CSSProperties = { padding: 16 };
  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridAutoRows: 'minmax(360px, auto)',
    gap: 16,
    alignItems: 'stretch',
  };

  const rightSidebar: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: SIDEBAR_W,
    height: '100vh',
    background: COLORS.panel2,
    borderLeft: `1px solid ${COLORS.border}`,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    zIndex: 10,
  };
  const sidebarBtn: React.CSSProperties = {
    padding: '6px 8px',
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: '#0f172a',
    color: COLORS.text,
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'right',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'all 0.2s ease',
    marginBottom: 6,
    opacity: 0.9,
  };

  const pill = (bg: string, col = COLORS.text): React.CSSProperties => ({
    padding: '4px 8px',
    borderRadius: 6,
    background: bg,
    color: col,
    fontSize: 11,
    border: `1px solid ${COLORS.border}`,
    fontWeight: 500,
  });

  /* ---------- FULL-SCREEN MAP BRANCH ---------- */
  if (mapFull) {
    const mapFullWrap: React.CSSProperties = {
      position: 'fixed', inset: 0, background: COLORS.bg, zIndex: 2000, display: 'grid', gridTemplateRows: '56px 1fr',
    };
    const mapTopBar: React.CSSProperties = {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 12px', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.panel2,
    };
    const mapCanvas: React.CSSProperties = {
      width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: '#0b1220',
    };

    return (
      <div dir="rtl" style={mapFullWrap}>
        <div style={mapTopBar}>
          <div style={{ fontWeight: 800 }}>نمایش تمام‌صفحه نقشه</div>
          <button
            type="button"  // ← prevent submit semantics
            onClick={() => setMapFull(false)}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${COLORS.accent}`,
              background: 'rgba(96,165,250,0.12)', color: COLORS.text, fontWeight: 800, cursor: 'pointer' }}
          >
            برگشت به داشبورد
          </button>
        </div>

        <div style={mapCanvas}>
          <img
            src={mapImg} alt="نقشه"
            style={{ width: '55%', height: 'auto', maxHeight: '60%', borderRadius: 12,
              border: `1px solid ${COLORS.border}`, objectFit: 'contain', boxShadow: '0 0 10px rgba(0,0,0,0.3)', display: 'block' }}
          />
        </div>
      </div>
    );
  }
  /* ------------------------------------------- */

  return (
    <div dir="rtl" style={pageStyle}>
      <div style={header}>
        <div style={{ justifySelf: 'start', display: 'flex', gap: 8 }}>
          {loading && <span style={pill('rgba(96,165,250,0.15)')}>در حال بارگذاری…</span>}
          {err && <span style={pill('rgba(248,113,113,0.15)', COLORS.danger)}>{err}</span>}
        </div>

        <div style={{ justifySelf: 'center' }}>
          <div style={titleBox}>
            <span style={{ fontWeight: 900, fontSize: '1.2rem' }}>
              داشبورد مدیریت نقاط ایزولاسیون و اسپیدها
            </span>
          </div>
        </div>

        <div />
      </div>

      {/* فیلترها + لوگوها */}
      <GlobalFilters
        value={filters}
        onChange={(next) => {
          const isManagerLocal = String(userInfo.Role) === '0';
          if (!isManagerLocal && userInfo.UnitArea) {
            next.units = [userInfo.UnitArea]; // UI label stays "واحد …"
          }
          setFilters(next);
        }}
        colors={COLORS}
        leftExtras={[
          logoSrc && (
            <img
              key="sm"
              src={logoSrc}
              alt="SM"
              style={{
                height: 90,
                width: 'auto',
                objectFit: 'contain',
                borderRadius: 12,
                background: 'transparent',
                border: 'none',
                marginLeft: 60,
                display: 'block',
              }}
            />
          ),
        ]}
      />

      {/* گرید 2 ستونه */}
      <div style={gridWrap}>
        <div style={grid}>
          <SpadesOverviewCard colors={COLORS} data={overview} />
          <MapCard colors={COLORS} onOpenFullMap={() => setMapFull(true)} />



          {/* <OperationReportsCard
            colors={COLORS}
            stats={stats}
            range={range}
            onSetRange={setRange}
            onRecentActivitiesClick={onRecentActivitiesClick}
          /> */}

          <RequestToExecutionCard
            colors={COLORS}
            buildUrlWithFilters={(base) => buildUrlWithFilters(base)}
            buildPostBody={buildStatsPostBody}
          />

          <PlateInventoryCard
            colors={COLORS}
            sizes={stats?.spades_information?.spades_size_counts}
          />

          <SystemHealthCard colors={COLORS} pill={(c:string)=>pill(c)} />

          <div style={{ minHeight: 360 }}>
            <ResponseTimeAveragesCard
              colors={COLORS}
              buildUrlWithFilters={(base) => buildUrlWithFilters(base)}
              buildPostBody={buildStatsPostBody}
            />
          </div>

          <div style={{ minHeight: 360 }}>
            <SpadesMostUsedCard
              colors={COLORS}
              buildUrlWithFilters={(base) => buildUrlWithFilters(base)}
              buildPostBody={buildStatsPostBody}
              limit={10}
            />
          </div>

          <div style={{ minHeight: 360 }}>
            <TopIsolationPointsCard
              colors={COLORS}
              buildUrlWithFilters={(base) => buildUrlWithFilters(base)}
              buildPostBody={buildStatsPostBody}
              limit={10}
            />
          </div>

          {/* <ReportsAuditCard colors={COLORS} /> */}
        </div>
      </div>

      {/* سایدبار ثابت */}
      <aside style={rightSidebar}>
        <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 6 }}>
          میانبرها و فیلترها • نقش: {roleName}{userUnit ? ` • واحد: ${userUnit}` : ''}
        </div>

        <button type="button" style={sidebarBtn} onClick={() => setMapFull(true)}>نمایش تمام‌صفحه نقشه</button>

        {!isManager && (
          <button type="button" style={sidebarBtn} onClick={onRecentActivitiesClick}>عملیات‌های من</button>
        )}

        <button type="button" style={sidebarBtn} onClick={openRequests}>
          درخواست‌ها (۴۸ ساعت اخیر)
        </button>

        <div style={{ marginTop: 'auto', fontSize: 12, color: COLORS.sub }}>
          نسخه {faNum('1.0')} • {faDate(new Date())}
        </div>
      </aside>

      {/* ─────── پنل/دیالوگِ درخواست‌ها ─────── */}
      {requestsOpen && (
        <div
          onClick={() => setRequestsOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2500,
            display: 'grid', placeItems: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'clamp(720px, 80vw, 1100px)',
              maxHeight: '80vh',
              overflow: 'auto',
              background: COLORS.panel,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 12px 40px rgba(0,0,0,.4)',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 900 }}>
                {isManager ? 'همهٔ درخواست‌ها (۴۸ ساعت اخیر / همهٔ واحدها)' : `درخواست‌های واحد ${userUnit || 'من'} (۴۸ ساعت اخیر)`}
              </div>
              <button
                type="button"  // ← prevent submit semantics
                onClick={() => setRequestsOpen(false)}
                style={{ ...sidebarBtn, marginBottom: 0 }}
              >
                بستن
              </button>
            </div>

            {requestsLoading && <div style={pill('rgba(96,165,250,0.15)')}>در حال دریافت…</div>}
            {requestsErr && <div style={pill('rgba(248,113,113,0.15)', COLORS.danger)}>{requestsErr}</div>}

            {!requestsLoading && !requestsErr && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#0b1220' }}>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>کد</th>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>واحد</th>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>عنوان</th>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>وضعیت</th>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>ثبت</th>
                    <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${COLORS.border}` }}>به‌روزرسانی</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 12, textAlign: 'center', color: COLORS.sub }}>
                        موردی یافت نشد.
                      </td>
                    </tr>
                  )}
                  {requests.map((r, i) => (
                    <tr key={`${r.id}-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: 8 }}>{faNum(r.id ?? '—')}</td>
                      <td style={{ padding: 8 }}>{r.unit || '—'}</td>
                      <td style={{ padding: 8 }}>{r.title || '—'}</td>
                      <td style={{ padding: 8 }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                          background: 'rgba(96,165,250,0.12)',
                        }}>
                          {r.status || '—'}
                        </span>
                      </td>
                      <td style={{ padding: 8 }}>{r.created_at ? faDate(r.created_at) : '—'}</td>
                      <td style={{ padding: 8 }}>{r.updated_at ? faDate(r.updated_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
